import { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { type BaseFilterCardProps, FilterCard } from '~/components/Filters/FilterCard';
import { SmartTagsFilter, type SmartTagsFilterProps } from '~/components/Filters/SmartTagsFilter/SmartTagsFilter';
import { type SmartTagBase } from '~/components/SmartTagSelect/utils';
import {
  clearSmartTagsFilterValuesAction,
  removeFilterAction,
  setSmartTagsFilterLogicAction,
  setSmartTagsFiltersValueAction,
} from '~/store/filters/actions';
import { selectedSmartTagsFilterLogicSelector, selectedSmartTagsFiltersSelector } from '~/store/filters/selectors';
import { type FilterLogicType, type SelectedFilter } from '~/store/filters/types';

export type SmartTagsFilterCardProps = BaseFilterCardProps & {
  SmartTagsSelect: SmartTagsFilterProps['SmartTagsSelect'];
};

export const SmartTagsFilterCard = memo(({ SmartTagsSelect, ...restOfProps }: SmartTagsFilterCardProps) => {
  const dispatch = useDispatch();

  const clearValues = useCallback(() => {
    dispatch(clearSmartTagsFilterValuesAction());
  }, [dispatch]);

  const selectedLogic = useSelector(selectedSmartTagsFilterLogicSelector);
  const selectedSmartTags = useSelector(selectedSmartTagsFiltersSelector);

  const onLogicChange = useCallback(
    (logic: FilterLogicType) => {
      dispatch(setSmartTagsFilterLogicAction({ logic }));
    },
    [dispatch],
  );

  const onSelectedTagsChanged = useCallback(
    (smartTags: SmartTagBase[]) => {
      dispatch(setSmartTagsFiltersValueAction({ smartTags }));
    },
    [dispatch],
  );

  const onRemoveFilter = useCallback(
    (filter: SelectedFilter) => {
      dispatch(removeFilterAction({ filter }));
      clearValues();
    },
    [clearValues, dispatch],
  );

  return (
    <FilterCard currentFilter="smartTags" onClearClick={clearValues} onRemoveFilter={onRemoveFilter} {...restOfProps}>
      <SmartTagsFilter
        SmartTagsSelect={SmartTagsSelect}
        selectedLogic={selectedLogic}
        selectedSmartTags={selectedSmartTags}
        onLogicChange={onLogicChange}
        onSelectedSmartTagsChanged={onSelectedTagsChanged}
      />
    </FilterCard>
  );
});

SmartTagsFilterCard.displayName = 'SmartTagsFilterCard';
